import { useEffect, useRef, useState } from "react";
import { rawRequest } from "../../lib/api";
import Button from "../Button";
import Card from "../Card";

/**
 * TicketPhotoCard — the weight-ticket photo display (SPEC-54 extraction 2).
 *
 * Hoisted from FinishedJobDetail + ReviewedJobDetail (token-identical
 * blocks: `photoUrl/photoLoading/photoError/lightbox` family, rawRequest blob
 * → `URL.createObjectURL` with revoke-on-unmount, 7 `loadGenRef` race-guard
 * refs per consumer = 14 total, thumbnail + boolean lightbox).
 *
 * The consumers' original mount effect branched on `job.ticketPhotoUrl`:
 * null → clearPhoto() client-side (the no-photo state, NO network request);
 * non-null → fetch the blob. `ticketPhotoUrl` restores that exact
 * short-circuit — when it is null the effect clears without fetching,
 * matching the original behavior exactly. A 404 (or other failure) during
 * the rare fetch path still falls back to clearPhoto/error box via the
 * original error handling. All 14 `loadGenRef` refs preserved.
 *
 * Renders a bare `<Card>` + sibling lightbox; grid wrappers (`office-col`)
 * stay inline in the consumers.
 */
export default function TicketPhotoCard({
  jobId,
  ticketPhotoUrl,
}: {
  jobId: string;
  ticketPhotoUrl: string | null;
}) {
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoLoading, setPhotoLoading] = useState(true);
  const [photoError, setPhotoError] = useState(false);
  const [lightbox, setLightbox] = useState(false);
  const photoUrlRef = useRef<string | null>(null);
  const loadGenRef = useRef(0);

  const clearPhoto = () => {
    if (photoUrlRef.current) {
      URL.revokeObjectURL(photoUrlRef.current);
      photoUrlRef.current = null;
    }
    setPhotoUrl(null);
    setPhotoLoading(false);
  };

  const loadPhoto = () => {
    setPhotoLoading(true);
    setPhotoError(false);
    const gen = ++loadGenRef.current;
    rawRequest(`/api/v1/jobs/${jobId}/photo`)
      .then(async (res) => {
        if (gen !== loadGenRef.current) return;
        if (!res.ok) {
          if (res.status === 404) {
            // Fallback: photo row served but R2 object gone (post-purge) —
            // mirror the no-photo state.
            clearPhoto();
          } else {
            setPhotoError(true);
            clearPhoto();
          }
          return;
        }
        const blob = await res.blob();
        if (gen !== loadGenRef.current) return;
        const objUrl = URL.createObjectURL(blob);
        if (photoUrlRef.current) {
          URL.revokeObjectURL(photoUrlRef.current);
        }
        photoUrlRef.current = objUrl;
        setPhotoUrl(objUrl);
        setPhotoLoading(false);
      })
      .catch(() => {
        if (gen !== loadGenRef.current) return;
        setPhotoError(true);
        clearPhoto();
      });
  };

  useEffect(() => {
    loadGenRef.current++;
    if (ticketPhotoUrl) {
      loadPhoto();
    } else {
      // Original short-circuit: no ticket photo → the no-photo state with
      // NO network request (the consumers' original `job`-gated effect).
      setPhotoError(false);
      clearPhoto();
    }
  }, [jobId, ticketPhotoUrl]);

  useEffect(() => {
    return () => {
      loadGenRef.current++;
      if (photoUrlRef.current) {
        URL.revokeObjectURL(photoUrlRef.current);
        photoUrlRef.current = null;
      }
    };
  }, []);

  return (
    <>
      <Card>
        <h2>Weight Ticket Photo</h2>
        {photoLoading && <p className="muted">Loading photo…</p>}
        {photoError && (
          <div className="photo-unavailable-box">
            <p className="err">Photo unavailable</p>
            <Button variant="secondary" onClick={loadPhoto}>
              Retry Photo
            </Button>
          </div>
        )}
        {!photoLoading && !photoError && photoUrl && (
          <div className="photo-preview-container">
            <img
              src={photoUrl}
              alt="Weight Ticket"
              className="photo-thumbnail"
              onClick={() => setLightbox(true)}
              title="Click to enlarge"
            />
            <p className="muted" style={{ fontSize: "12px", marginTop: "6px" }}>
              Click photo to enlarge
            </p>
          </div>
        )}
        {!photoLoading && !photoError && !photoUrl && (
          <p className="muted">No photo uploaded.</p>
        )}
      </Card>

      {lightbox && photoUrl && (
        <div className="lightbox-overlay" onClick={() => setLightbox(false)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={photoUrl} alt="Weight ticket enlarged" />
            <button
              type="button"
              className="lightbox-close"
              onClick={() => setLightbox(false)}
            >
              ✕ Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}